import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

// Enhanced animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(60px);
    filter: blur(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8) rotate(-5deg);
    filter: blur(5px);
  }
  to {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    filter: blur(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(2deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 191, 255, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 20px rgba(0, 191, 255, 0);
  }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const GalleryContainer = styled.div`
  padding: 6rem 0;
  background: linear-gradient(135deg, #fafbff 0%, #f4f8ff 50%, #eef4ff 100%);
  position: relative;

  @media (max-width: 1024px) {
    padding: 5rem 0;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }

  @media (max-width: 480px) {
    padding: 3rem 0;
  }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    padding: 0 18px;
  }

  @media (max-width: 768px) {
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const AchievementBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(0, 191, 255, 0.1);
  color: var(--primary-blue);
  padding: 0.8rem 1.6rem;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 2.5rem;
  border: 1px solid rgba(0, 191, 255, 0.2);
  box-shadow: 0 4px 15px rgba(0, 191, 255, 0.1);
  animation: ${fadeInUp} 0.8s ease-out;

  &::before {
    content: "";
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 0.7rem;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffa726'%3E%3Cpath d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'/%3E%3C/svg%3E") no-repeat center;
    background-size: contain;
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.2rem;
  font-weight: 900;
  color: var(--text-primary);
  text-align: center;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #1a1a1a, #333333, #1a1a1a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${fadeInUp} 1s ease-out 0.2s both;
  text-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  letter-spacing: -0.02em;

  &::after {
    content: "";
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #00bfff, #1e90ff, #0066cc);
    border-radius: 2px;
    animation: ${fadeInScale} 0.8s ease-out 0.8s both;
    box-shadow: 0 2px 10px rgba(0, 191, 255, 0.4);
  }

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }

  @media (max-width: 480px) {
    font-size: 2.2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  color: var(--text-secondary);
  text-align: center;
  max-width: 750px;
  margin: 0 auto 4rem;
  line-height: 1.8;
  animation: ${fadeInUp} 1s ease-out 0.4s both;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin: 0 auto 3rem;
    max-width: 600px;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const AwardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
  margin-top: 4rem;

  @media (max-width: 1024px) {
    gap: 2rem;
  }

  @media (max-width: 768px) {
    gap: 1.8rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const MediaNewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.8rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }
`;

const MediaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.1), rgba(255, 87, 87, 0.05));
  color: #e53e3e;
  padding: 0.8rem 1.6rem;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 2.5rem;
  border: 1px solid rgba(255, 107, 107, 0.2);
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.1);
  animation: ${fadeInUp} 0.8s ease-out;

  &::before {
    content: "";
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 0.7rem;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23e53e3e'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/%3E%3C/svg%3E") no-repeat center;
    background-size: contain;
  }
`;

const MediaNewsContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInScale} 0.8s ease-out both;

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    border-color: rgba(0, 191, 255, 0.2);
  }

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
  &:nth-child(5) { animation-delay: 0.5s; }
`;

const MediaNewsImage = styled.img`
  width: 100%;
  height: 280px;
  object-fit: contain;
  padding: 1rem;
  background: #f8f9fa;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  ${MediaNewsContainer}:hover & {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    height: 250px;
    padding: 0.8rem;
  }

  @media (max-width: 480px) {
    height: 220px;
    padding: 0.6rem;
  }
`;

const MediaNewsOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 2rem 1.5rem 1.5rem;
  transform: translateY(100%);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  ${MediaNewsContainer}:hover & {
    transform: translateY(0);
  }
`;

const MediaNewsTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: white;
`;

const SectionDivider = styled.div`
  margin: 6rem 0;
  position: relative;
  
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 191, 255, 0.3), transparent);
    transform: translateY(-50%);
  }

  @media (max-width: 768px) {
    margin: 4rem 0;
  }

  @media (max-width: 480px) {
    margin: 3rem 0;
  }
`;

const AwardContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 25px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 8px 25px rgba(0, 191, 255, 0.1);
  border: 1px solid rgba(0, 191, 255, 0.15);
  cursor: pointer;
  animation: ${fadeInScale} 0.8s ease-out both;

  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
  &:nth-child(4) {
    animation-delay: 0.4s;
  }
  &:nth-child(5) {
    animation-delay: 0.5s;
  }
  &:nth-child(6) {
    animation-delay: 0.6s;
  }
  &:nth-child(7) {
    animation-delay: 0.7s;
  }
  &:nth-child(8) {
    animation-delay: 0.8s;
  }
`;

const AwardImage = styled.img`
  width: 100%;
  height: 320px;
  object-fit: contain;
  padding: 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    height: 280px;
    padding: 1.8rem;
  }

  @media (max-width: 768px) {
    height: 250px;
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    height: 220px;
    padding: 1.2rem;
  }
`;

const AwardOverlay = styled.div`
  display: none;
`;

const AwardLabel = styled.div`
  display: none;
`;

const FloatingElements = styled.div`
  display: none;
`;

// Modal Components
const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeInScale} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  padding: 2rem;
  will-change: opacity, transform;
  transform: translateZ(0); /* Force hardware acceleration */

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.98),
    rgba(255, 255, 255, 0.95)
  );
  backdrop-filter: blur(25px);
  border-radius: 25px;
  padding: 2.5rem;
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(0, 191, 255, 0.2);
  cursor: default;
  animation: ${fadeInScale} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, opacity;
  transform: translateZ(0); /* Force hardware acceleration */

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.9));
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 1.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10000;
  backdrop-filter: blur(10px);

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(255, 0, 0, 0.8),
      rgba(255, 0, 0, 0.9)
    );
    transform: scale(1.1) rotate(90deg);
    box-shadow: 0 5px 20px rgba(255, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    width: 40px;
    height: 40px;
    font-size: 1.4rem;
  }
`;

const ModalImage = styled.img`
  width: 100%;
  max-height: 75vh;
  object-fit: contain;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  will-change: transform;
  transform: translateZ(0); /* Force hardware acceleration */
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ModalCaption = styled.p`
  text-align: center;
  margin-top: 1.5rem;
  color: var(--text-primary);
  font-weight: 700;
  font-size: 1.1rem;
  background: linear-gradient(135deg, transparent, rgba(0, 191, 255, 0.1));
  padding: 1rem;
  border-radius: 10px;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-top: 1rem;
    padding: 0.8rem;
  }
`;

// Intersection Observer Hook
const useInView = (options) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [options]);

  return [ref, isInView];
};

const AnimatedSection = ({ children, ...props }) => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      style={{ opacity: isInView ? 1 : 0.5, transition: "opacity 0.8s ease" }}
      {...props}
    >
      {children}
    </div>
  );
};

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const awards = [
    {
      src: require("../assets/1.jpg"),
      alt: "",
    },
    {
      src: require("../assets/2.jpg"),
      alt: "",
    },
    {
      src: require("../assets/3.jpg"),
      alt: "",
    },
    {
      src: require("../assets/4.jpg"),
      alt: "",
    },
    {
      src: require("../assets/5.jpg"),
      alt: "",
    },
    {
      src: require("../assets/6.jpg"),
      alt: "",
    },
    {
      src: require("../assets/7.jpg"),
      alt: "",
    },
    {
      src: require("../assets/8.jpg"),
      alt: "",
    },
  ];

  const mediaNews = [
    {
      src: require("../assets/ads in newspaper/1000225415.jpg"),
      alt: "ASKK International in Media - News Coverage 1",
      title: "Media Coverage 1"
    },
    {
      src: require("../assets/ads in newspaper/1000225416.jpg"),
      alt: "ASKK International in Media - News Coverage 2",
      title: "Media Coverage 2"
    },
    {
      src: require("../assets/ads in newspaper/1000225417.jpg"),
      alt: "ASKK International in Media - News Coverage 3",
      title: "Media Coverage 3"
    },
    {
      src: require("../assets/ads in newspaper/1000225418.jpg"),
      alt: "ASKK International in Media - News Coverage 4",
      title: "Media Coverage 4"
    },
    {
      src: require("../assets/ads in newspaper/1000225424.jpg"),
      alt: "ASKK International in Media - News Coverage 5",
      title: "Media Coverage 5"
    },
  ];

  // Close modal with escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && selectedImage) {
        setSelectedImage(null);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [selectedImage]);

  return (
    <GalleryContainer>

      <AnimatedSection>
        <Section>
          <AchievementBadge>Excellence & Recognition</AchievementBadge>
          <SectionTitle>Awards & Achievements</SectionTitle>
          <SectionSubtitle>
            Celebrating our journey of excellence, recognition, and unwavering
            commitment to advancing women's health and empowerment across India
          </SectionSubtitle>

          <AwardsGrid>
            {awards.map((award, index) => (
              <AwardContainer
                key={index}
                onClick={() => setSelectedImage(award)}
              >
                <AwardImage src={award.src} alt={award.alt} loading="lazy" />
                <AwardOverlay />
                <AwardLabel>{award.title}</AwardLabel>
              </AwardContainer>
            ))}
          </AwardsGrid>
        </Section>
      </AnimatedSection>

      <SectionDivider />

      <AnimatedSection>
        <Section>
          <MediaBadge>Press & Publications</MediaBadge>
          <SectionTitle>Media and News</SectionTitle>
          <SectionSubtitle>
            Our presence in leading newspapers and media outlets, showcasing our commitment
            to transparency, community engagement, and industry leadership
          </SectionSubtitle>

          <MediaNewsGrid>
            {mediaNews.map((media, index) => (
              <MediaNewsContainer
                key={index}
                onClick={() => setSelectedImage(media)}
              >
                <MediaNewsImage src={media.src} alt={media.alt} loading="lazy" />
                <MediaNewsOverlay>
                  <MediaNewsTitle>{media.title}</MediaNewsTitle>
                </MediaNewsOverlay>
              </MediaNewsContainer>
            ))}
          </MediaNewsGrid>
        </Section>
      </AnimatedSection>

      {/* Enhanced Modal */}
      {selectedImage && (
        <ImageModal onClick={() => setSelectedImage(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setSelectedImage(null)}>×</CloseButton>
            <ModalImage src={selectedImage.src} alt={selectedImage.alt} />
            <ModalCaption>{selectedImage.title}</ModalCaption>
          </ModalContent>
        </ImageModal>
      )}
    </GalleryContainer>
  );
};

export default Gallery;
